import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NGQ_LANGUAGES_TOKEN } from '@app-core/injection-tokens/languages.token';
import { ITranslationLanguage } from '@app-core/interfaces/language.interface';
import {
  EmailLanguageType,
  emailLanguageToLanguageCodeMap,
  languageCodeToEmailLanguageTypeMap,
} from '@app-core/interfaces/preferred-email-language.interface';
import { LanguageService } from '@app-core/services/language.service';
import { LanguageRepository } from '@app-core/state/language.repository';
import { SessionRepository } from '@app-core/state/session.repository';
import {
  LanguageCode,
  LanguageCodes,
} from '@app-shared/enums/language-codes.enum';
import { emitOnce, filterNil } from '@ngneat/elf';
import { TranslocoModule } from '@ngneat/transloco';
import { switchMap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'sandoz-select-language',
  templateUrl: './select-language.component.html',
  styleUrls: ['./select-language.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TranslocoModule,
    ReactiveFormsModule,

    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
  ],
})
export class SelectLanguageComponent {
  private readonly languageRepo = inject(LanguageRepository);
  private readonly languageService = inject(LanguageService);

  protected readonly sessionRepo = inject(SessionRepository);
  protected readonly languagesList =
    inject<ITranslationLanguage[]>(NGQ_LANGUAGES_TOKEN);
  protected readonly availableLanguages = this.languagesList.filter(
    language => language.id !== LanguageCode.CS_CZ,
  );

  appLangControl: FormControl<LanguageCodes | null>;
  emailLangControl: FormControl<LanguageCodes | null>;

  constructor() {
    this.emailLangControl = new FormControl<LanguageCodes | null>(null);

    const activeLangCode = this.languageRepo.getActiveLanguageCode();
    this.sessionRepo.authUser$.subscribe(res => {
      const emailLang = res?.email_language as EmailLanguageType | undefined;
      const langCode = emailLang
        ? (emailLanguageToLanguageCodeMap.get(emailLang) ?? null)
        : null;

      this.emailLangControl.setValue(langCode);
    });

    this.appLangControl = new FormControl(activeLangCode);

    this.appLangControl.valueChanges
      .pipe(filterNil(), takeUntilDestroyed())
      .subscribe(lang => {
        emitOnce(() => {
          this.languageRepo.setActiveLanguageByCode(lang!);
          this.languageRepo.setHasUserChosenLanguage(true);
        });
      });

    this.emailLangControl.valueChanges
      .pipe(
        filterNil(),
        switchMap(lang =>
          this.languageService.setPreferredEmailLanguage(
            languageCodeToEmailLanguageTypeMap.get(lang!),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
}
